import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { IMAGES, LINKS, CONTENT } from "../const/config";
import { detectDevice } from "../utils/helper";
import { useStoreLinks } from "../hooks/useStoreLinks";

import { trackDownload, trackSocial } from "../analytics/tracking";

const SOCIAL_ICONS = [
  { icon: IMAGES.iconTiktok, href: LINKS.social.tiktok, network: "tiktok" },
  { icon: IMAGES.iconYoutube, href: LINKS.social.youtube, network: "youtube" },
  { icon: IMAGES.iconFacebook, href: LINKS.social.facebook, network: "facebook" },
  { icon: IMAGES.iconInstagram, href: LINKS.social.instagram, network: "instagram" },
  { icon: IMAGES.iconThreads, href: LINKS.social.threads, network: "threads" },
];

function StoreQR({ value, alt }) {
  return (
    <div className="w-32 h-32 mb-4 rounded-s shadow-lg bg-white flex items-center justify-center">
      {value ? (
        <QRCodeSVG value={value} size={120} level="M" aria-label={alt} />
      ) : (
        <div
          className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"
          role="status"
          aria-label="Đang tải QR"
        />
      )}
    </div>
  );
}

export default function Footer() {
  const [device, setDevice] = useState(null);
  const { links: storeLinks } = useStoreLinks();

  useEffect(() => {
    setDevice(detectDevice());
  }, []);

  const showAndroid = device !== "ios";
  const showIOS = device !== "android";

  const handleDownloadClick = (platform) => {
    trackDownload(platform, 'store');
  };

  const handleSocialClick = (network) => {
    trackSocial(network);
  }

  return (
    <footer
      className="bg-blue-600 text-white pt-4 text-sm"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      {/* ───── LOGO + Description ───── */}
      <div className="flex justify-center mb-6 px-4">
        <img
          src={IMAGES.footerPC}
          alt="footer"
          className="hidden min-[1080px]:block w-full max-w-4xl"
        />
        <img
          src={IMAGES.footerMobile}
          alt="footer"
          className="block min-[1080px]:hidden w-full max-w-[1080px]"
        />
      </div>

      {/* ───── DOWNLOAD APP ───── */}
      <div className="flex justify-center mb-12 px-4">
        <div className={`grid gap-6 sm:gap-10 ${showAndroid && showIOS ? "grid-cols-2" : "grid-cols-1"}`}>
          {showAndroid && (
            <div className="flex flex-col items-center">
              <StoreQR value={storeLinks.android} alt="Android QR" />

              <a
                href={storeLinks.android || LINKS.android.store}
                target="_blank"
                rel="noreferrer"
                className="hover:opacity-80 transition-opacity"
                onClick={() => handleDownloadClick("android")}
              >
                <div className="flex items-center gap-2 w-44 py-2 px-3 rounded-md bg-blue-600 border border-white shadow-lg">
                  <img
                    src={IMAGES.googlePlay}
                    className="w-8 h-8 object-contain"
                    alt="Google Play"
                  />

                  <div className="flex flex-col leading-tight text-left">
                    <span className="text-[10px] font-semibold text-white uppercase tracking-wide">
                      Tải ngay
                    </span>

                    <span className="text-xs font-semibold text-white">
                      Google Play
                    </span>
                  </div>
                </div>
              </a>
            </div>
          )}

          {showIOS && (
            <div className="flex flex-col items-center">
              <StoreQR value={storeLinks.ios} alt="iOS QR" />

              <a
                href={storeLinks.ios || LINKS.ios.store}
                target="_blank"
                rel="noreferrer"
                className="hover:opacity-80 transition-opacity"
                onClick={() => handleDownloadClick("ios")}
              >
                <div className="flex items-center gap-2 w-44 py-2 px-3 rounded-md bg-blue-600 border border-white shadow-lg">
                  <img
                    src={IMAGES.appStore}
                    className="w-8 h-8 object-contain"
                    alt="App Store"
                  />

                  <div className="flex flex-col leading-tight text-left">
                    <span className="text-[10px] font-semibold text-white uppercase tracking-wide">
                      Tải ngay
                    </span>

                    <span className="text-xs font-semibold text-white">
                      App Store
                    </span>
                  </div>
                </div>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* ───── CONTACT ───── */}
      <div className="flex items-center justify-center gap-4 mb-10">
        <p className="leading-relaxed opacity-90 text-justify">
          Kết nối với COLASCORE
        </p>
        <div className="flex gap-3 md:text-lg">
          {SOCIAL_ICONS.map((item, i) => (
            <a
              key={i}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              onClick={() => handleSocialClick(item.network)}
            >
              <img
                src={item.icon}
                alt={item.network}
                className="w-6 h-6 object-contain"
              />
            </a>
          ))}
        </div>
      </div>

      {/* ───── COPYRIGHT ───── */}
      <div className="border-t border-white/20 text-center py-4 text-sm opacity-80">
        © {new Date().getFullYear()} {CONTENT.footer.copyright}
      </div>
    </footer>
  );
}